import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { UserPlus, Target } from "lucide-react";
import { MORE_CASTINGS_PL_TEMPLATE } from "@/constants/moreCastingsPLTemplate";

/**
 * Renders the static "Zwiększ swoje szanse na castingi w Polsce" template
 * content (10 steps, PL only) inside GoalDetail when
 * goal.template === "more_castings_pl". Persists per-goal user checkbox state
 * via the existing PATCH /api/career/goals/{id} endpoint (template_data field).
 */
export default function MoreCastingsPLGoalContent({ goal, onGoalUpdated }) {
  const L = MORE_CASTINGS_PL_TEMPLATE.pl;
  // checklists[stepIdx] = { [i]: true } — Step 01 main + subsection use own buckets
  const [checklists, setChecklists] = useState({});
  const [subChecklists, setSubChecklists] = useState({});
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const bootRef = useRef(false);

  useEffect(() => {
    const td = goal?.template_data || {};
    setChecklists(td.checklists || {});
    setSubChecklists(td.subChecklists || {});
    bootRef.current = true;
  }, [goal?.goal_id]);

  const schedulePatch = (nextMain, nextSub) => {
    if (!bootRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.patch(`/career/goals/${goal.goal_id}`, {
          template_data: { checklists: nextMain, subChecklists: nextSub },
        });
        onGoalUpdated?.(r.data);
      } catch {
        // 401 handled by interceptor; other errors retry on next click.
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  const toggleMain = (stepIdx, i) => {
    setChecklists((prev) => {
      const step = { ...(prev[stepIdx] || {}) };
      step[i] = !step[i];
      const next = { ...prev, [stepIdx]: step };
      schedulePatch(next, subChecklists);
      return next;
    });
  };
  const toggleSub = (stepIdx, i) => {
    setSubChecklists((prev) => {
      const step = { ...(prev[stepIdx] || {}) };
      step[i] = !step[i];
      const next = { ...prev, [stepIdx]: step };
      schedulePatch(checklists, next);
      return next;
    });
  };

  return (
    <div className="mt-2" data-testid="more-castings-pl-goal-content">
      <div className="flex items-center gap-2 mb-6">
        <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase flex items-center gap-1">
          <Target size={11} /> {L.eyebrow}
        </div>
        {saving && (
          <span className="mono text-[10px] uppercase tracking-widest text-stone-500">
            {L.savingLabel}
          </span>
        )}
      </div>

      <div className="paper p-6 border-amber-500/30 bg-amber-500/5">
        <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line">
          {L.intro}
        </p>
      </div>

      <div className="space-y-12 mt-10">
        {L.steps.map((step, idx) => {
          const stepBucket = checklists[idx] || {};
          const subBucket = subChecklists[idx] || {};
          return (
            <motion.section
              key={idx}
              data-testid={`mc-step-${idx + 1}`}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
                {L.stepLabel} {String(idx + 1).padStart(2, "0")}
              </div>
              <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{step.title}</h2>

              {step.body && (
                <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
                  {step.body.split(/(https?:\/\/[^\s]+)/g).map((chunk, i) =>
                    /^https?:\/\//.test(chunk) ? (
                      <a
                        key={i}
                        href={chunk}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-amber-400 underline hover:text-amber-300 break-all"
                      >
                        {chunk}
                      </a>
                    ) : (
                      <span key={i}>{chunk}</span>
                    )
                  )}
                </div>
              )}

              {step.checklist && (
                <ul className="mt-5 space-y-2">
                  {step.checklist.map((label, i) => (
                    <li key={i}>
                      <label className="paper p-3 flex items-start gap-3 cursor-pointer hover:border-amber-500/50">
                        <input
                          type="checkbox"
                          data-testid={`mc-check-${idx + 1}-${i}`}
                          checked={!!stepBucket[i]}
                          onChange={() => toggleMain(idx, i)}
                          className="w-4 h-4 accent-amber-500 mt-0.5"
                        />
                        <span className={`text-sm ${stepBucket[i] ? "text-stone-500 line-through" : "text-stone-200"}`}>
                          {label}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              {step.reflection && (
                <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm italic">
                  {step.reflection}
                </div>
              )}

              {step.subsection && (
                <div className="mt-6 paper p-5 border-amber-500/25">
                  <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
                    Sekcja
                  </div>
                  <h3 className="serif text-xl text-stone-100 mt-1">{step.subsection.heading}</h3>
                  {step.subsection.body && (
                    <p className="text-stone-300 text-sm leading-relaxed mt-3 whitespace-pre-line">
                      {step.subsection.body}
                    </p>
                  )}
                  {step.subsection.checklist && (
                    <ul className="mt-4 space-y-2">
                      {step.subsection.checklist.map((label, i) => (
                        <li key={i}>
                          <label className="paper p-3 flex items-start gap-3 cursor-pointer hover:border-amber-500/50">
                            <input
                              type="checkbox"
                              data-testid={`mc-subcheck-${idx + 1}-${i}`}
                              checked={!!subBucket[i]}
                              onChange={() => toggleSub(idx, i)}
                              className="w-4 h-4 accent-amber-500 mt-0.5"
                            />
                            <span className={`text-sm ${subBucket[i] ? "text-stone-500 line-through" : "text-stone-200"}`}>
                              {label}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  {step.subsection.after && (
                    <p className="text-stone-300 text-sm leading-relaxed mt-4 whitespace-pre-line">
                      {step.subsection.after}
                    </p>
                  )}
                </div>
              )}

              {step.collapsibles && step.collapsibles.map((sec, si) => (
                <details
                  key={si}
                  data-testid={`mc-collapse-${idx + 1}-${si}`}
                  className="group mt-4 rounded-lg border border-stone-800/60 bg-stone-900/40"
                >
                  <summary className="list-none cursor-pointer flex items-center justify-between p-4 select-none">
                    <span className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                      Rozwiń: {sec.title}
                    </span>
                    <span className="text-stone-400 text-xs group-open:hidden">▾</span>
                    <span className="text-stone-400 text-xs hidden group-open:inline">▴</span>
                  </summary>
                  <div className="border-t border-stone-800/60 p-4 text-stone-300 text-sm leading-relaxed whitespace-pre-line">
                    {sec.body.split(/(https?:\/\/[^\s]+)/g).map((chunk, ci) =>
                      /^https?:\/\//.test(chunk) ? (
                        <a
                          key={ci}
                          href={chunk}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-400 underline hover:text-amber-300 break-all"
                        >
                          {chunk}
                        </a>
                      ) : (
                        <span key={ci}>{chunk}</span>
                      )
                    )}
                  </div>
                </details>
              ))}

              {step.followupTask && (
                <div className="mt-6 paper p-5 border-amber-500/50 bg-amber-500/10">
                  <div className="mono text-[10px] tracking-[0.35em] text-amber-400 uppercase mb-2">
                    Obowiązkowe zadanie
                  </div>
                  <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line">
                    {step.followupTask}
                  </p>
                </div>
              )}

              {step.crmLink && (
                <div className="mt-5">
                  <Link
                    to="/crm"
                    data-testid={`mc-add-contact-${idx + 1}`}
                    className="pill-btn !py-2 !text-xs inline-flex"
                  >
                    <UserPlus size={12} /> {step.crmLinkLabel || L.addContactToCrm}
                  </Link>
                </div>
              )}

              {step.finale && (
                <div className="mt-6 paper p-5 border-amber-500/40 bg-amber-500/5 text-amber-100 text-sm leading-relaxed whitespace-pre-line">
                  {step.finale}
                </div>
              )}
            </motion.section>
          );
        })}
      </div>
    </div>
  );
}
